import './WixFeatures.scss';

import React, { useEffect, useState, useRef } from 'react';

import {
  Box,
  Button,
  Buttonbar,
  ButtonbarButton,
  ButtonbarButtonLabel,
  Card,
  Carousel,
  CarouselSlide,
  Column,
  Divider,
  FlexBox,
  GridContainer,
  H2,
  H3,
  Hidden,
  Icon,
  Li,
  Ol,
  P,
  Row,
  SelectionSet,
  SelectionSetInput,
  SelectionSetLabel,
  useScreenClass,
  Visible,
} from '@vp/swan';

import {
  FeatureGroupModel,
  FeatureModel,
  HeroModel,
} from '@vp/digital-common-data-model-lib';

import FluidImageLoading from './FluidImageLoading';
import MissingContentAlert from '../../common/MissingContentAlert';

type WixFeaturesProps = {
  content: HeroModel;
  accessibility: FeatureGroupModel;
};

export default function WixFeatures({
  content,
  accessibility,
}: WixFeaturesProps): JSX.Element {
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0);
  const interval = useRef(null);

  if (!content) {
    clearInterval(interval.current);

    return <MissingContentAlert />;
  }

  const selectedCategory: FeatureGroupModel =
    content.heroMetadata[selectedCategoryIndex];
  const selectedFeature: FeatureModel =
    selectedCategory?.features[selectedFeatureIndex];

  const handleChangeCategory = (index: number) => {
    setSelectedCategoryIndex(index);
    handleChangeFeature(0);
  };

  const handleChangeFeature = (index: number) => {
    setSelectedFeatureIndex(index);
  };

  return (
    <GridContainer>
      <Row>
        <Column span={12}>
          <H2 fontSkin="title-section" fontSize="x2large">
            {content.title}
          </H2>

          <P my={0}>{content.descriptionLong}</P>
        </Column>
      </Row>

      <Row
        mt={'between-subsections'}
        mb={{
          xs: 'between-subsections',
          sm: '0',
          md: '0',
          lg: '9',
        }}
      >
        <Column span={12}>
          <Visible xs>
            <SelectionSet
              variant="single-select"
              skin="buttons"
              className="wix-pdp-c-page__new-features__selections"
              defaultSelectedValue={String(selectedCategoryIndex)}
            >
              {content.heroMetadata.map((featureGroup, index) => (
                <SelectionSetInput
                  key={index}
                  value={String(index)}
                  onClick={() => {
                    handleChangeCategory(index);
                  }}
                >
                  <SelectionSetLabel mb={0}>
                    {featureGroup.title}
                  </SelectionSetLabel>
                </SelectionSetInput>
              ))}
            </SelectionSet>
          </Visible>

          <Hidden xs>
            <Buttonbar
              variant="single-select"
              defaultSelectedValue={String(selectedCategoryIndex)}
              width="full-width"
            >
              {content.heroMetadata.map((featureGroup, index) => (
                <ButtonbarButton
                  key={index}
                  value={String(index)}
                  onClick={() => {
                    handleChangeCategory(index);
                  }}
                >
                  <ButtonbarButtonLabel>
                    {featureGroup.title}
                  </ButtonbarButtonLabel>
                </ButtonbarButton>
              ))}
            </Buttonbar>
          </Hidden>
        </Column>
      </Row>

      <Visible xs>
        <Row>
          <Column span={12} mt={0}>
            <Carousel
              slidesToShow={1.0}
              arrows={false}
              peek={false}
              dots={true}
              infinite={true}
              currentSlide={selectedFeatureIndex}
              gridGutters={true}
              accessibleTextForNext={accessibility.features[4].title}
              accessibleTextForPrevious={accessibility.features[3].title}
              aria-roledescription={accessibility.features[0].title}
              aria-label={content.description}
              accessibleTextForDots={
                selectedCategory?.features.length > 0
                  ? selectedCategory.features.map(
                      (feature, index) =>
                        accessibility.features[5]?.title.replace(
                          '{{SLIDE_NUMBER}}',
                          String(index + 1)
                        ) ?? ''
                    )
                  : []
              }
              accessibleTextForVisibleSlides={
                accessibility.features[2]?.hoverText
              }
              accessibleTextForSlide={accessibility.features[2]?.altText}
            >
              {selectedCategory?.features &&
                selectedCategory.features.map(
                  (feature: FeatureModel, index: number) => {
                    return (
                      <CarouselSlide
                        key={feature?.title}
                        aria-roledescription={accessibility.features[1].title}
                      >
                        <Card bordered evenHeight>
                          <FlexBox
                            flexDirection="column"
                            justifyContent="space-between"
                            className="wix-pdp-c-page__features__image-container"
                          >
                            <Box textAlign={'left'}>
                              <H3 fontSkin={'title-subsection'}>
                                {feature.title}
                              </H3>

                              <P fontSkin={'body-standard'} pb={2} mt={3}>
                                {feature.description}
                              </P>
                            </Box>

                            <FluidImageLoading
                              media={feature.icons[0]}
                              alt=""
                              sizes="(max-width: 7232px) 100vw, 3616px"
                            />
                          </FlexBox>
                        </Card>
                      </CarouselSlide>
                    );
                  }
                )}
            </Carousel>
          </Column>
        </Row>
      </Visible>

      <Visible sm md>
        <Row>
          <Column span={8} offset={2} my={7}>
            <FluidImageLoading
              media={selectedFeature?.icons[0]}
              alt=""
              sizes="(max-width: 7232px) 50vw, 3616px"
            />
          </Column>

          {selectedCategory?.features &&
            selectedCategory.features.map((feature, index) => (
              <Column
                key={feature.title}
                span={4}
                onClick={() => {
                  handleChangeFeature(index);
                }}
                className={
                  selectedFeatureIndex !== index
                    ? 'wix-pdp-c-page__new-features__disabled'
                    : ''
                }
              >
                <H3 fontSkin={'title-subsection'}>
                  {index + 1}. &nbsp; {feature.title}
                </H3>

                <P fontSkin={'body-standard'} my={0} mb={4}>
                  {feature.description}
                </P>
              </Column>
            ))}
        </Row>
      </Visible>

      <Visible xl lg>
        <Row>
          <Column span={5} spanLg={5} spanXl={6}>
            <FlexBox style={{ height: '100%' }}>
              <Ol className="wix-pdp-c-page__new-features">
                {selectedCategory?.features &&
                  selectedCategory.features.map((feature, index) => (
                    <Li
                      key={feature.title}
                      onClick={() => {
                        handleChangeFeature(index);
                      }}
                      fontSkin={'title-subsection'}
                      className={
                        selectedFeatureIndex !== index
                          ? 'wix-pdp-c-page__new-features__disabled'
                          : ''
                      }
                    >
                      <H3 fontSkin={'title-subsection'}>{feature.title}</H3>

                      <P fontSkin={'body-standard'} my={0}>
                        {feature.description}
                      </P>
                      {index !== selectedCategory.features.length - 1 && (
                        <Divider />
                      )}
                    </Li>
                  ))}
              </Ol>
            </FlexBox>
          </Column>

          <Column span={7} spanMd={8} spanLg={7} spanXl={6}>
            <FluidImageLoading
              alt=""
              media={selectedFeature?.icons[0]}
              sizes="(max-width: 7232px) 50vw, 3616px"
            />
          </Column>
        </Row>
      </Visible>
    </GridContainer>
  );
}
